<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import { useMapGetter } from 'dashboard/composables/store';
import { canViewMasifiWaDeletedOriginal } from 'dashboard/helper/masifi/masifiWaMessageActions';
import { useMessageContext } from '../provider.js';
import MasifiWaDeletedOriginal from './MasifiWaDeletedOriginal.vue';

// Nota bajo la burbuja para lo que el cliente hizo en WhatsApp y Chatwoot no tiene UI nativa (F2 ruling 10):
// "Editado", "El cliente eliminó este mensaje" (en un grupo: "Su autor eliminó este mensaje", F3a ruling 15; el
// contenido se conserva para el agente) y "Reacción eliminada" (la
// fila de una reacción que se quitó: native delete que conserva `masifi_wa_reaction`). Ocupa el área `meta`
// de la grilla de Message.vue (igual que MessageError); la alineación llega como clase desde el touchpoint.
// Clases de texto = las de MessageMeta (text-xs + text-n-slate-11), sin colores propios.
// "Ver original" (F3a E2E fix 4): el backend guarda el PRIMER texto original en `masifi_wa_previous_content`; el toggle
// (Button nativo) lo muestra bajo el texto actual, atenuado. Solo si existe y difiere del actual (1:1, grupos, ecos).
// El salto de línea es un ítem flex `basis-full` vacío: así el original hereda la alineación (start/end) de la fila.
// "Ver original" de un ELIMINADO (follow-up del PR #192): con la marca `masifiWaOriginalKept` y solo para un administrador o
// el autor, con el módulo activo (MasifiWaDeletedOriginal lo pide al endpoint al tocar). Rol y cuenta se leen del store
// SOLO si hay marca (computed
// perezoso: la nota suelta de los specs no necesita store).
const props = defineProps({
  contentAttributes: { type: Object, default: null },
  content: { type: String, default: null },
});

const { t } = useI18n();
// El texto actual y los content attributes salen del contexto nativo del mensaje (provideMessageContext de
// Message.vue): el montaje queda en `<Component :is="masifiWa.note" :class>`, sin props (E2E fix 8, regla 11c;
// core-slim T10). Las props ganan si vienen (uso suelto / specs).
const messageContext = (() => {
  try {
    return useMessageContext();
  } catch {
    return null;
  }
})();
const currentContent = computed(
  () => props.content ?? messageContext?.content?.value ?? null
);
const attrs = computed(
  () =>
    props.contentAttributes ?? messageContext?.contentAttributes?.value ?? {}
);
const showOriginal = ref(false);
const currentRole = useMapGetter('getCurrentRole');
const getAccount = useMapGetter('accounts/getAccount');
const accountId = useMapGetter('getCurrentAccountId');
const deletedOriginalId = computed(() => {
  if (!attrs.value.deleted || !attrs.value.masifiWaOriginalKept) return null;
  const visible = canViewMasifiWaDeletedOriginal({
    sender: messageContext?.sender?.value,
    currentUserId: messageContext?.currentUserId?.value,
    role: currentRole.value,
    features: getAccount.value(accountId.value)?.features,
  });
  return visible ? (messageContext?.id?.value ?? null) : null;
});

const isRemovedReaction = a => a.deleted && a.masifiWaReaction;
const previousContent = computed(() => {
  const previous = attrs.value.masifiWaPreviousContent;
  if (!attrs.value.masifiWaEdited || !previous) return null;
  return previous === currentContent.value ? null : previous;
});
</script>

<template>
  <div
    v-if="
      attrs.masifiWaEdited ||
      attrs.masifiWaDeletedByContact ||
      isRemovedReaction(attrs) ||
      deletedOriginalId
    "
    class="[grid-area:meta] flex flex-wrap items-center gap-x-1.5 mt-1 text-xs text-n-slate-11"
  >
    <span v-if="attrs.masifiWaDeletedByContact">
      {{
        attrs.masifiWaAuthor
          ? t('MASIFI_WHATSAPP_QR.GROUPS.NOTE.DELETED_BY_AUTHOR')
          : t('MASIFI_WHATSAPP_QR.MESSAGE_NOTE.DELETED_BY_CONTACT')
      }}
    </span>
    <span v-if="isRemovedReaction(attrs)">
      {{ t('MASIFI_WHATSAPP_QR.MESSAGE_NOTE.REACTION_REMOVED') }}
    </span>
    <span v-if="attrs.masifiWaEdited">
      {{ t('MASIFI_WHATSAPP_QR.MESSAGE_NOTE.EDITED') }}
    </span>
    <template v-if="previousContent">
      <Button
        link
        slate
        xs
        data-test="masifi-wa-original-toggle"
        :label="
          showOriginal
            ? t('MASIFI_WHATSAPP_QR.MESSAGE_NOTE.HIDE_ORIGINAL')
            : t('MASIFI_WHATSAPP_QR.MESSAGE_NOTE.SHOW_ORIGINAL')
        "
        @click="showOriginal = !showOriginal"
      />
      <template v-if="showOriginal">
        <span class="basis-full" />
        <p
          data-test="masifi-wa-original-content"
          class="max-w-full mt-1 mb-0 text-sm whitespace-pre-line break-words border-s-2 border-n-weak ps-2 text-n-slate-11"
        >
          {{ previousContent }}
        </p>
      </template>
    </template>
    <MasifiWaDeletedOriginal
      v-if="deletedOriginalId"
      :key="deletedOriginalId"
      :message-id="deletedOriginalId"
    />
  </div>
</template>
